/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from '../lib/a11y.js';
import { createAccordion } from '../lib/accordion.js';
import { formatTimes } from '../lib/time.js';
import { browser } from '../lib/browser.js';
import { getConfig } from '../lib/config.js';
import {
    MAP_STATE_COOKIE,
    NODES_VISITED_LIST_COOKIE,
} from '../lib/constants.js';
import { contentPreprocessors } from '../lib/content-preprocessors.js';
import { cookie, cookieConsent } from '../lib/cookie.js';
import { initMenu, keepAspectRatio, pinScroll } from '../lib/dom.js';
import { emit, on, onTarget } from '../lib/events.js';
import { Lightbox } from '../lib/lightbox.js';
import {
    getRelativeLink,
    injectParams,
    isCurrentMapURL,
    isEmbedded,
    isExternalLink,
    stripPathForApp,
} from '../lib/links.js';
import { KontextMap } from '../lib/map.js';
import { Keyboard } from '../lib/map-keyboard.js';
import { renderMath } from '../lib/math.js';
import { linksToMediaPlayer, rewriteMediaURL } from '../lib/media.js';
import {
    getGlossary,
    getPopupInstances,
    setPopupInstances,
} from '../lib/instances.js';
import { stripHTML } from '../lib/text.js';
import { smartStripLineBreaks } from '../lib/util.js';

/*
 * Builds the map and wires the page to it.
 *
 * This runs last: everything it touches has to exist first.
 */

/**
 * The image lightbox, built the first time a picture is opened.
 *
 * It was kept on the `Kontextlab` global so the popup content handlers
 * could reach it; it is module state now.
 */
let mediaLightbox;

/** Subscribe one handler to the same event on both kinds of popup. */
function onBothPopups(manager, panel, name, handler) {
    onTarget(manager, name, handler);
    onTarget(panel, name, handler);
}

    
export async function init() {
    /* Map */

    const cfg = getConfig();
    let accessToken =
        new URLSearchParams(location.search).get('access') ??
        cfg.access?.token ??
        null;
    const isPreview = !!cfg.map.isPreview;
    const isEmbedOnly = !!cfg.map.isEmbedOnly;

    if (accessToken && accessToken === 'null') {
        accessToken = null;
    }

    // Stop early if this map can only be embedded
    if (!isPreview && isEmbedOnly && !isEmbedded) {
        console.warn('Map content can only be shown when embedded');
        return;
    }

    const language =
        cfg.i18n?.lang ?? document.documentElement.dataset.lang ?? 'und';
    const mainLanguage =
        cfg.i18n?.mainLang ??
        document.documentElement.dataset.mainLang ??
        'und';

    const translateStr = (value) =>
        value && typeof value === 'object'
            ? value[language] ?? ''
            : value ?? '';

    const mapElem = document.getElementById('map');
    /* The map is clipped, and everything drawn over it -- the zoom
       controls, the zoom levels, the popup panel -- is positioned inside
       that clipped box. Focusing a point the box does not currently show
       makes the browser scroll it, which carries all of that away with it,
       so the map keeps the scroll the panning does not use. */
    pinScroll(mapElem);
    /* The map is named by the address, so it is not a parameter. */
    const mapDataURL = injectParams(cfg.endpoints.mapData, {
        access: accessToken,
        lang: cfg.i18n.lang,
        preview: cfg.map.isPreview ? 1 : null,
        rewrite: cfg.map.rewrite,
        v: cfg.map.version || null
    });
    const mapURL = location.pathname === '/' ? '' : location.pathname;

    let storageResponse;
    try {
        const r = await fetch(mapDataURL, { credentials: 'same-origin' });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        storageResponse = await r.json();
    } catch (e) {
        console.error(`Could not load map from ${mapDataURL}`, e);
        return;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    let maxScale;
    let stepsPerLevel = null;
    if (windowWidth <= 480 || windowHeight <= 480) {
        maxScale = 5;
    } else if (windowWidth <= 768 || windowHeight <= 768) {
        maxScale = 5;
    } else if (navigator.platform.toUpperCase().indexOf('MAC') >= 0) {
        maxScale = 5;
        stepsPerLevel = 14;
    } else {
        maxScale = 3;
    }

    const mapData = storageResponse;
    const mapId = mapData.map.id;
    const map = KontextMap.init(mapElem, mapData, {
        modelWidth: mapData.map.measurement?.width || 1280,
        modelHeight: mapData.map.measurement?.height || 800,
        zoomLevels: mapData.map.visibility_levels,
        maxScale:
            Math.exp(Math.log(maxScale) / 12) **
            (mapData.map.visibility_levels || 12),
        stepsPerLevel: stepsPerLevel,
        language: language,
        fallbackLanguage: mainLanguage,
        additionalParams:
            accessToken && accessToken.length > 0
                ? { access: accessToken }
                : {}
    });
    const mapModel = document.querySelector('.map-model');
    const nodeElems = mapModel ? mapModel.querySelectorAll('.node') : [];
    const nodeElemsById = {};
    nodeElems.forEach((el) => {
        if (el?.__data__) nodeElemsById[el.__data__.id] = el;
    });

    const zoomBy = (increment) => {
        return (event) => {
            event.preventDefault();
            const center = map.center();
            map.centerTo(
                center[0],
                center[1],
                map.zoomLevel() + increment,
                false
            );
        };
    };

    const zoomTo = (zoomLevel) => {
        return (event) => {
            event.preventDefault();
            const width = map.width();
            const height = map.height();
            map.centerTo(width / 2.0, height / 2.0, zoomLevel, true);
        };
    };

    const nodes = mapData.nodes || [];
    const articlePattern = /^(.*)(#a=|\/article\/)(\d+)/;
    const matchArticleLink = (linkURL) => {
        if (!linkURL || isExternalLink(linkURL)) {
            return null;
        }
        return linkURL.match(articlePattern);
    };

    /**
     * Give a point the role, name and keyboard handling of a control.
     *
     * The points are `div` elements produced by d3 and cannot be
     * re-tagged without reworking the renderer, so the semantics are
     * applied here, where it is decided that a point does anything.
     *
     * `tabindex="-1"` on purpose: a map can hold hundreds of points and
     * each would otherwise be its own tab stop. The visually hidden
     * points list is the sequential route through them; from the map
     * itself they are reached with the arrow keys.
     */
    const makeNodeOperable = (node, nodeElem, role) => {
        nodeElem.setAttribute('role', role);
        nodeElem.setAttribute('tabindex', '-1');
        if (role === 'button') {
            nodeElem.setAttribute('aria-haspopup', 'dialog');
            nodeElem.setAttribute('aria-expanded', 'false');
        }
        const title = stripHTML(
            smartStripLineBreaks(
                translateStr(node.content.title) || ''
            )
        ).trim();
        nodeElem.setAttribute(
            'aria-label',
            title || cfg.uiText?.a11y?.unnamedPoint || ''
        );
        nodeElem.addEventListener('keydown', (event) => {
            if (!a11y?.isActivationKey(event)) return;
            event.preventDefault();
            // Delegating to click keeps one behaviour for both input
            // methods, whichever of the several handlers is bound.
            nodeElem.click();
        });
    };

    /** Keep `aria-expanded` truthful across the whole map. */
    const setExpandedPoint = (elem) => {
        mapElem
            .querySelectorAll('.node[aria-expanded="true"]')
            .forEach((n) => n.setAttribute('aria-expanded', 'false'));
        elem?.setAttribute?.('aria-expanded', 'true');
    };

    /* Nodes with direct links */
    nodes.forEach((node) => {
        const nodeId = String(node.id);
        let linkURL = translateStr(node.content.node_link_to);
        if (!linkURL) return;

        const nodeElem = nodeElemsById[nodeId];
        if (!nodeElem) {
            console.warn(
                `Could not find DOM element for node with id ${nodeId}`
            );
            return;
        }
        nodeElem.dataset.href = linkURL;
        nodeElem.classList.add('node--link');
        makeNodeOperable(node, nodeElem, 'link');

        if (isExternalLink(linkURL)) {
            nodeElem.addEventListener('click', (event) => {
                event.preventDefault();
                if (getComputedStyle(nodeElem).opacity !== '0')
                    window.open(linkURL, '_blank');
            });
            nodeElem.dataset.href = linkURL;
            nodeElem.dataset.target = '_blank';
            nodeElem.title = linkURL;
        } else {
            linkURL = rewriteMediaURL(linkURL, {
                defaultVariant: '',
                useLegacy: true
            });
            nodeElem.dataset.href = linkURL;
            // Do not inject URL parameters if the URL points to another map
            if (isCurrentMapURL(linkURL)) {
                if (accessToken) {
                    linkURL = injectParams(linkURL, {
                        access: accessToken
                    });
                }
                try {
                    const previewURL = new URL(linkURL, window.location);
                    if (!isPreview) {
                        previewURL.searchParams.delete('preview');
                        linkURL = previewURL.toString();
                    } else if (
                        isPreview &&
                        !previewURL.searchParams.has('preview')
                    ) {
                        previewURL.searchParams.append('preview', '1');
                        linkURL = previewURL.toString();
                    }
                } catch (e) {
                    console.error(e);
                }
            }
            linkURL = getRelativeLink(
                stripPathForApp(linkURL)
            );
            if (!matchArticleLink(linkURL)) {
                try {
                    const targetURL = new URL(linkURL, window.location);
                    let linkTarget = '_self'; // ES6: let
                    if (targetURL.searchParams.has('_target')) {
                        linkTarget = `_${targetURL.searchParams.get('_target')}`;
                        targetURL.searchParams.delete('_target');
                        linkURL = targetURL.toString();
                        nodeElem.dataset.href = linkURL;
                        nodeElem.dataset.target = linkTarget;
                    }
                    nodeElem.addEventListener('click', (event) => {
                        event.preventDefault();
                        if (getComputedStyle(nodeElem).opacity !== '0')
                            window.open(linkURL, linkTarget);
                    });
                } catch (e) {
                    console.error(e);
                }
            }
        }
    });

    /* Popups */
    const popupPanel = new KontextMap.PopupPanel(
        mapElem,
        map,
        mapData,
        language,
        cfg.uiText,
        cfg.map
    );
    const popupManager = new KontextMap.PopupManager(
        mapElem,
        map,
        mapData,
        language,
        cfg.uiText,
        cfg.map
    );
    let curPopupElem; // ES6: let
    setPopupInstances(popupPanel, popupManager);

    // Iterate over all popups and assign handlers to the appropriate DOM
    // elements they are associated with
    /**
     * Report whether activating this point leads anywhere.
     *
     * Shared with the points list below: a point listed there but not
     * wired up here would be a dead entry, and a point wired up but not
     * listed would be unreachable without a mouse.
     */
    const nodeHasContent = (node) =>
        !!(
            translateStr(node.content.content) ||
            translateStr(node.content.external_content_url) ||
            translateStr(node.content.marginal_content_1) ||
            translateStr(node.content.marginal_content_2) ||
            matchArticleLink(translateStr(node.content.node_link_to))
        );

    nodes.forEach((node) => {
        const nodeId = String(node.id);
        const linkURL = translateStr(node.content.node_link_to);
        const articleMatch = matchArticleLink(linkURL);
        if (!nodeHasContent(node)) {
            return;
        }
        const nodeElem = nodeElemsById[nodeId];
        if (!nodeElem) {
            console.warn(
                `Could not find DOM element for node with id ${nodeId}`
            );
            return;
        }
        if (node.content.popup_type === null) {
            node.content.popup_type = 'popup-panel';
        }
        const isPopupPanel =
            ['popup-panel', 'popup-panel-large'].indexOf(
                node.content.popup_type
            ) !== -1;
        const isDetachedPopup =
            ['popup', 'popup-large', 'popup-medium', 'popup-small'].indexOf(
                node.content.popup_type
            ) !== -1;
        let popupType; // ES6: let
        let popupAction; // ES6: let
        if (articleMatch || isPopupPanel) {
            popupType = 'has-popup-panel';
            popupAction = function (event) {
                event.preventDefault();
                const clickedSame = curPopupElem === this;
                if (!clickedSame) {
                    popupManager.hide(true);
                    const options = {};
                    if (articleMatch) {
                        let articleMapURL = articleMatch[1]; // ES6: let
                        const articleId = articleMatch[3];

                        articleMapURL =
                            stripPathForApp(articleMapURL);
                        options.externalContent = `${getRelativeLink(articleMapURL).replace(/#.*$/, '')}/article/${articleId}`;
                        if (accessToken || isPreview) {
                            options.externalContent =
                                injectParams(
                                    options.externalContent,
                                    {
                                        access: accessToken || null,
                                        preview: isPreview ? 1 : null
                                    }
                                );
                        }
                        options.popupType = 'popup-panel-full';
                    } else if (
                        this.__data__.content.popup_type ===
                        'popup-panel-large'
                    ) {
                        options.popupType =
                            this.__data__.content.popup_type;
                    }
                    if (
                        translateStr(node.content.external_content_url)
                            ?.length
                    ) {
                        options.externalContent = `${translateStr(node.content.external_content_url)}`;
                    }
                    options.moveFocus = true;
                    popupPanel.showFor(this, options);
                    curPopupElem = this;
                    setExpandedPoint(this);
                } else {
                    popupPanel.hide();
                    curPopupElem = undefined;
                    setExpandedPoint();
                }
            };
        } else if (isDetachedPopup) {
            popupType = 'has-popup-detached';
            popupAction = function (event) {
                event.preventDefault();
                const clickedSame = curPopupElem === nodeElem;
                if (!clickedSame) {
                    popupPanel.hide(true);
                    const options = {};
                    const popupType = this.__data__.content.popup_type;
                    if (
                        popupType === 'popup' ||
                        popupType === 'popup-large' ||
                        popupType === 'popup-medium' ||
                        popupType === 'popup-small'
                    ) {
                        options.popupType =
                            popupType === 'popup'
                                ? 'popup-large'
                                : popupType;
                    }
                    options.moveFocus = true;
                    popupManager.showFor(nodeElem, options);
                    curPopupElem = nodeElem;
                    setExpandedPoint(nodeElem);
                } else {
                    popupManager.hide();
                    curPopupElem = undefined;
                    setExpandedPoint();
                }
            };
        } else {
            console.error(`Unknown popup type: ${node.content.popup_type}`);
        }
        nodeElem.classList.add('has-popup', popupType);
        makeNodeOperable(node, nodeElem, 'button');
        nodeElem.addEventListener('click', popupAction);
    });

    /* Keyboard navigation between points.

       The map is one tab stop, not one per point: a map can hold
       hundreds, and making each its own stop would bury the rest of the
       page behind them. Inside it the arrow keys move, which is the
       usual arrangement for a composite widget and is why the sequential
       order the points are drawn in does not have to be meaningful. */
    // Reached through `KontextMap.Keyboard` when everything hung off one
    // global. Nothing put it there once the modules arrived, so the guard
    // below was always false and the arrow keys did nothing.
    const keyboard = Keyboard;
    if (keyboard) {
        const operable = () =>
            nodes.filter((node) => {
                const elem = nodeElemsById[String(node.id)];
                return elem?.hasAttribute('role');
            });
        const ordered = keyboard.readingOrder(operable());
        const elementFor = (node) => nodeElemsById[String(node.id)];
        const inRange = () =>
            ordered.filter((node) =>
                keyboard.visibleAtLevel(node, map.zoomLevel())
            );

        /* How far inside the shown area a point has to sit to count as
           visible, as a share of each side. A point right at the edge is
           drawn half outside it. */
        const VIEW_INSET = 0.1;

        /* Move the map so the point is shown, rather than letting the
           browser reveal it by scrolling the clipped container. Panning is
           what the map does everywhere else, and it is the only way that
           leaves the chrome where it was drawn. */
        const reveal = (node) => {
            const x = Number(node?.geometry?.center?.x);
            const y = Number(node?.geometry?.center?.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return;
            const view = map.viewport();
            const insetX = view.width * VIEW_INSET;
            const insetY = view.height * VIEW_INSET;
            const shown =
                x >= view.x + insetX &&
                x <= view.x + view.width - insetX &&
                y >= view.y + insetY &&
                y <= view.y + view.height - insetY;
            if (shown) return;
            map.centerTo(x, y, undefined, !a11y?.prefersReducedMotion());
        };

        let current = null;
        /* `pan` says whether the map may move to show the point. It moves
           for a keypress, which is the reader asking to go there, and not
           for a zoom, which has already settled what is shown. */
        const setCurrent = (node, { focus = true, pan = true } = {}) => {
            if (!node) return;
            for (const other of ordered) {
                elementFor(other)?.setAttribute('tabindex', '-1');
            }
            const elem = elementFor(node);
            if (!elem) return;
            elem.setAttribute('tabindex', '0');
            current = node;
            if (focus) {
                if (pan) reveal(node);
                elem.focus({ preventScroll: true });
            }
        };

        // One point carries the tab stop from the start, so the map is
        // reachable before any arrow key has been pressed.
        setCurrent(inRange()[0] || ordered[0], { focus: false });

        mapElem.addEventListener('keydown', (event) => {
            if (event.altKey || event.ctrlKey || event.metaKey) return;
            const visible = inRange();
            if (!visible.length) return;

            if (keyboard.DIRECTION_VECTORS[event.key]) {
                const next = keyboard.nearestInDirection(
                    current,
                    visible,
                    event.key
                );
                if (next) {
                    event.preventDefault();
                    setCurrent(next);
                }
                return;
            }
            switch (event.key) {
                case 'Home':
                    event.preventDefault();
                    setCurrent(visible[0]);
                    break;
                case 'End':
                    event.preventDefault();
                    setCurrent(visible[visible.length - 1]);
                    break;
                case '+':
                case 'PageUp':
                    event.preventDefault();
                    zoomBy(1)(event);
                    break;
                case '-':
                case 'PageDown':
                    event.preventDefault();
                    zoomBy(-1)(event);
                    break;
                case '0': {
                    event.preventDefault();
                    const viewport = map.viewport();
                    map.centerTo(
                        viewport.width / 2,
                        viewport.height / 2,
                        1,
                        !a11y?.prefersReducedMotion()
                    );
                    break;
                }
                default:
                    break;
            }
        });

        // A point that leaves the current zoom level stops being
        // focusable, so the tab stop has to move with it. It goes to
        // whatever sits nearest the middle of the shown area, and takes
        // the map nowhere: a zoom is how the map arrives at the position
        // a direct link, a popup or a control asked for, and revealing a
        // point outside that position overrides it -- with the same
        // point every time, so every link would end at one place.
        on('map:zoom', () => {
            if (
                current &&
                keyboard.visibleAtLevel(current, map.zoomLevel())
            ) {
                return;
            }
            setCurrent(keyboard.nearestToView(inRange(), map.viewport()), {
                focus: mapElem.contains(document.activeElement),
                pan: false
            });
        });
    }

    // Zoom in on double click
    mapElem.addEventListener('dblclick', zoomBy(1));

    // Close popup when interacting with other map elements
    ['mousedown', 'touchstart', 'mousewheel'].forEach((type) => {
        mapElem.addEventListener(
            type,
            (event) => {
                const t = event.target || event.srcElement;

                // If the target is a text node, step up to an Element
                const el = t && t.nodeType === 1 ? t : t?.parentElement;
                if (!el) return;

                const inside = !!el.closest?.('.has-popup, .popup-panel');

                if (!inside) {
                    popupManager.hide(true);
                    popupPanel.hide(true);
                    curPopupElem = undefined;
                }
            },
            false
        );
    });

    const addAccessToken = (root) => {
        // images/audio/video/src + sources
        root.querySelectorAll(
            'img[src],audio[src],video[src],audio>source[src],video>source[src]'
        ).forEach((el) => {
            if (!isExternalLink(el.src)) {
                el.src = injectParams(el.src, {
                    access: accessToken
                });
            }
        });
        root.querySelectorAll('a[href]').forEach((a) => {
            if (!isExternalLink(a.href)) {
                a.href = injectParams(a.href, {
                    access: accessToken
                });
            }
        });
    };

    const makeAccordionList = function () {
        const prev = this.previousElementSibling;
        const shouldBeAccordion =
            (this.classList.contains('accordion') &&
                !this.parentElement?.classList.contains('accordion')) ||
            prev?.classList.contains('start-accordion');

        // `createAccordion` declines a list it has already built, so there
        // is nothing to check for here beyond whether this list wants to
        // be one at all.
        if (shouldBeAccordion) {
            createAccordion(this, { collapsible: true, active: -1 });
        }
    };

    // Handler to show images in popup contents in an overlay/lightbox
    const mediaLightboxHandler = (content) => {
        let root; // ES6: let
        if (typeof content === 'string') {
            const span = document.createElement('span');
            span.innerHTML = content;
            root = span;
        } else {
            root = content;
        }

        // Remove width/height, apply inline styles
        const imgs = root.querySelectorAll('img, picture');
        for (let i = 0; i < imgs.length; i++) {
            imgs[i].removeAttribute('width');
            imgs[i].removeAttribute('height');
            // The lightbox is filled from the trigger's own outerHTML,
            // which carries the attributes that made it a button. Inside
            // the overlay it is just the picture.
            imgs[i].removeAttribute('role');
            imgs[i].removeAttribute('tabindex');
            imgs[i].removeAttribute('aria-label');
            imgs[i].style.alignSelf = 'center';
            imgs[i].style.display = 'inline-block';
            imgs[i].style.maxHeight = '100%';
        }
        if (root.children) {
            for (let j = 0; j < root.children.length; j++) {
                root.children[j].style.cursor = 'zoom-out';
            }
        }

        const stopAndClose = (ev) => {
            if (ev?.preventDefault) ev.preventDefault();
            mediaLightbox.close();
        };
        root.addEventListener('click', stopAndClose);

        // Escape alone would satisfy the keyboard, but a dialog should
        // show the way out rather than expect it to be guessed.
        if (root.querySelector && !root.querySelector('.lightbox__close')) {
            const closeWrap = document.createElement('div');
            closeWrap.className = 'lightbox__close';
            const closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.innerHTML = '&times;';
            closeBtn.setAttribute(
                'aria-label',
                cfg.uiText?.a11y?.closeImage || ''
            );
            closeBtn.addEventListener('click', stopAndClose);
            closeWrap.appendChild(closeBtn);
            root.appendChild(closeWrap);
        }
    };

    if (mediaLightbox === undefined) {
        mediaLightbox = new Lightbox(mapElem, {
            header: document.getElementById('header'),
            onShow: mediaLightboxHandler,
            ariaLabel: cfg.uiText?.a11y?.imageViewer
        });
    }

    // --- Shared helpers for popup content (panel & detached) ---
    function sizeEmbeddedMedia(popupElem, inTransition) {
        const media = popupElem.querySelectorAll('iframe, video');
        media.forEach((embedded) => {
            const container = embedded.closest
                ? embedded.closest('main > *')
                : null;
            embedded.setAttribute('loading', 'lazy');
            if (embedded.tagName === 'IFRAME') {
                const src = embedded.getAttribute('src') || '';
                if (
                    !/(youtu\.be|youtube\.com|vimeo\.com)/.test(src) &&
                    !/\bvideo\b/.test(embedded.className)
                ) {
                    return; // skip non-video iframes
                }
            } else {
                embedded.setAttribute('preload', 'none');
            }
            const apply = () => {
                keepAspectRatio(
                    embedded,
                    16 / 9,
                    'width',
                    container
                );
            };
            if (inTransition) {
                const once = () => {
                    apply();
                    popupElem.removeEventListener(
                        browser.transitionEndEvent,
                        once,
                        false
                    );
                };
                popupElem.addEventListener(
                    browser.transitionEndEvent,
                    once,
                    false
                );
            } else {
                apply();
            }
        });
    }

    function attachImageLightbox(popupElem) {
        popupElem.querySelectorAll('img, picture').forEach((node) => {
            node.style.cursor = 'zoom-in';
            // An <img> carrying a click handler is invisible to the
            // keyboard. Only announce the ones that actually open the
            // lightbox; where a surrounding link wins the click, that
            // link is already the focusable control.
            const probeImg = node.querySelector?.('img') || node;
            const probeLink = node.closest?.('a');
            const linkWins =
                probeLink?.href &&
                probeImg.src &&
                probeLink.href.indexOf(probeImg.src) !== 0;
            if (!linkWins) {
                node.setAttribute('role', 'button');
                node.setAttribute('tabindex', '0');
                if (!node.getAttribute('aria-label')) {
                    node.setAttribute(
                        'aria-label',
                        cfg.uiText?.a11y?.enlargeImage || ''
                    );
                }
                // Keydown only: the click path below is already bound.
                node.addEventListener('keydown', (event) => {
                    if (!a11y?.isActivationKey(event)) {
                        return;
                    }
                    event.preventDefault();
                    mediaLightbox.show(node.outerHTML);
                });
            }
            node.addEventListener('click', (event) => {
                const img = node.querySelector?.('img');
                const imageEl = img || node;
                let p = node;
                let parentLink = null;
                while (p) {
                    if (p.tagName === 'A') {
                        parentLink = p;
                        break;
                    }
                    p = p.parentNode;
                }
                const sameTarget =
                    parentLink &&
                    imageEl &&
                    parentLink.href &&
                    imageEl.src &&
                    parentLink.href.indexOf(imageEl.src) === 0;
                if (!parentLink || sameTarget) {
                    event.preventDefault();
                    mediaLightbox.show(node.outerHTML);
                }
            });
        });
    }

    function setPopupAccentColor(root, accentColor) {
        root.querySelectorAll(
            'main > *, .popup-panel-social > *, nav > *'
        ).forEach((el) => {
            el.style.borderColor = accentColor;
        });
    }

    function processPopupContentCommon(
        popupElem,
        popupContentList,
        inTransition
    ) {
        // Collapsible sections on plain lists
        popupContentList.forEach((section) => {
            section.querySelectorAll('ul').forEach((ul) => {
                makeAccordionList.call(ul);
            });
        });
        if (typeof linksToMediaPlayer === 'function') {
            try {
                linksToMediaPlayer(popupElem);
            } catch {
                // Author-supplied markup: a link that cannot become a
                // player stays a link, which is still usable.
            }
        }
        sizeEmbeddedMedia(popupElem, !!inTransition);
        attachImageLightbox(popupElem);
        popupContentList.forEach(addAccessToken);
        popupContentList.forEach(renderMath);
        popupContentList.forEach((el) => {
            getGlossary()?.linkTerms(el);
        });
    }

    // A popup opened before the glossary arrived has no terms marked up,
    // and the popup is not rebuilt when it does. `linkTerms` skips what
    // is already marked, so a second pass over open content is safe.
    on('glossary:loaded', () => {
        document
            .querySelectorAll(
                '.popup-content-main, .popup-content-marginal'
            )
            .forEach((el) => getGlossary()?.linkTerms(el));
    });

    // TODO: Refactor to use contentPreprocessors logic
    onTarget(popupManager, 'show.popup', function (point) {
            clearTimeout(mapStateTimeoutId);
            popupPanel.hide(true);

            const popupElem = document.querySelector('.popup-detached');
            if (!popupElem) return;

            const popupContent = Array.prototype.slice.call(
                popupElem.querySelectorAll(
                    '.popup-content-main, .popup-content-marginal'
                )
            );
            processPopupContentCommon(
                popupElem,
                popupContent,
                /*inTransition*/ false
            );

            saveMapState();
            this.reloadSocialMedia(point.__data__);
    });

    // TODO: Refactor to use contentPreprocessors logic
    onTarget(popupPanel, 'show.popup', function (point, options) {
            clearTimeout(mapStateTimeoutId);
            popupManager.hide(true);
            const popupElem = document.querySelector('.popup-panel');
            if (!popupElem) return;
            const popupContent = Array.prototype.slice.call(
                popupElem.querySelectorAll(
                    '.popup-content-main, .popup-content-marginal'
                )
            );
            processPopupContentCommon(
                popupElem,
                popupContent,
                typeof this.isInTransition === 'function' &&
                    this.isInTransition()
            );

            // Set popup accent colors depending on the selected point
            const borderElem = point.querySelector('.border-container');
            const borderColor = borderElem
                ? borderElem.style['border-color']
                : point.style['border-color'];
            const bgColor = point.style['background-color'];
            let accentColor = borderColor || bgColor;
            if (
                /^(#ffffff|#fff|rgba\(255, *255, *255, *[\d.]+\)|rgb\(255, *255, *255\)|white)$/.test(
                    accentColor
                )
            ) {
                accentColor = '#000';
            }
            setPopupAccentColor(popupElem, accentColor);

            // Reposition popup
            popupPanel.position(() => {
                saveMapState();
                this.reloadSocialMedia(point.__data__);
                setPopupAccentColor(popupElem, accentColor);
            }, options);
    });

    // Insert external content items
    onBothPopups(
        popupManager,
        popupPanel,
        'show.popup',
        // The point and its options are not needed: the popup is already
        // rendered, and this fills the external content blocks in it.
        () => {
            const popupElem = document.querySelector('.popup');
            if (!popupElem) {
                console.warn('No popup element found');
                return;
            }
            const externalContentItemUrlBase =
                cfg.endpoints.externalContentItem;
            const externalContentLoadingLabel =
                cfg.uiText.externalContent.loading;
            const externalContentLoadingFailedLabel =
                cfg.uiText.externalContent.failed;
            const externalContents = popupElem.querySelectorAll(
                'div[data-external-provider]'
            );
            externalContents.forEach((externalContentElem) => {
                const externalContentProvider =
                    externalContentElem.dataset.externalProvider;
                const externalContentItemId =
                    externalContentElem.dataset.externalId;
                // A provider can hold several connections and their item
                // ids come from the same system, so without naming the
                // one this block was embedded from the backend resolves
                // it against the first — and the popup shows another
                // space's page. A block written before connections
                // existed carries none, and the first connection is
                // exactly what it always resolved against.
                const externalContentConnection =
                    externalContentElem.dataset.externalConnection;
                let externalContentItemUrl = externalContentItemUrlBase
                    .replace('__provider_id__', externalContentProvider)
                    .replace('__item_id__', externalContentItemId);
                if (externalContentConnection) {
                    externalContentItemUrl = injectParams(
                        externalContentItemUrl,
                        { connection: externalContentConnection }
                    );
                }
                externalContentElem.innerHTML =
                    externalContentLoadingLabel || 'Loading content …';
                fetch(externalContentItemUrl, {
                    credentials: 'same-origin'
                })
                    .then((r) => {
                        if (!r.ok) throw r;
                        // Items endpoint returns HTML fragments; parse as text
                        return r.json();
                    })
                    .then((response) => {
                        // Run preprocessors over the HTML string (blocks external media if needed)
                        const html = response.content;
                        const safe = contentPreprocessors
                            ?.applyToHTML
                            ? contentPreprocessors.applyToHTML(
                                  html
                              )
                            : html;
                        externalContentElem.innerHTML = safe;

                        externalContentElem
                            .querySelectorAll('ul.accordion')
                            .forEach((accordionList) => {
                                createAccordion(accordionList, {
                                    collapsible: true,
                                    active: -1
                                });
                            });
                        // A block's timestamps are UTC and the reader
                        // wants their own zone. This was guarded on a
                        // `window.dayjs` the page stopped loading when
                        // the Map Viewer moved off the old bundle, so it had
                        // quietly not been running.
                        formatTimes(externalContentElem);
                    })
                    .catch((error) => {
                        externalContentElem.innerHTML =
                            externalContentLoadingFailedLabel ||
                            'Content could not be loaded.';
                        console.error(error);
                    });
            });
        }
    );

    // Reposition popup if window gets resized.
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (curPopupElem) popupPanel.position();
        }, 1000);
    });

    // Set glossary style for popups based on global design
    const getGlossaryStyle = () => {
        const glossaryDesign = mapData.design?.tooltips || {};
        const cssOptions = {};
        if (glossaryDesign.font_url)
            cssOptions.fontFamily = glossaryDesign.font_url;
        if (glossaryDesign.font_size)
            cssOptions.fontSize = `${glossaryDesign.font_size}px`;
        if (glossaryDesign.font_color)
            cssOptions.color = glossaryDesign.font_color;
        return cssOptions;
    };
    getGlossary()?.setCssOptions(getGlossaryStyle());

    const getMapState = () => {
        const center = map.center();
        const zoomLevel = map.zoomLevel();
        const popupId =
            popupPanel.sourceId === undefined
                ? popupManager.sourceId
                : popupPanel.sourceId;
        return {
            x: center[0],
            y: center[1],
            zoomLevel: zoomLevel,
            popupId:
                popupId !== undefined
                    ? Number.parseInt(popupId, 10)
                    : undefined
        };
    };
    const equalStates = (state1, state2) => {
        if ((!state1 || !state2) && state1 !== state2) {
            return false;
        }
        if (state1.popupId !== undefined || state2.popupId !== undefined) {
            return state1.popupId === state2.popupId;
        }
        const deltaX = Math.abs(state2.x - state1.x);
        const deltaY = Math.abs(state2.y - state1.y);
        return (
            state1.zoomLevel === state2.zoomLevel &&
            deltaX < 1e-5 &&
            deltaY < 1e-5
        );
    };
    const setMapState = (state, animated) => {
        map.centerTo(state.x, state.y, state.zoomLevel, animated);
    };
    const serializeMapState = (state) => {
        const popupId =
            popupPanel.sourceId === undefined
                ? popupManager.sourceId
                : popupPanel.sourceId;
        if (popupId !== undefined) {
            return `p=${Number.parseInt(popupId, 10)}`;
        }
        const serializedParts = [];
        if (state.zoomLevel || state.x || state.y) {
            serializedParts.push(
                `m=${[
                    (state.zoomLevel || 1).toFixed(0),
                    (state.x || 0).toFixed(5).replace(/\.?0+$/, ''),
                    (state.y || 0).toFixed(5).replace(/\.?0+$/, '')
                ].join('/')}`
            );
        }
        return serializedParts.join(',');
    };
    const deserializeMapState = (serializedState) => {
        const serializedParts = serializedState.split(',');
        const serializedData = {};
        serializedParts.forEach((part) => {
            const keyValue = part.split('=');
            serializedData[keyValue[0]] = keyValue[1];
        });
        const state = {};
        if ('m' in serializedData) {
            const mapParts = serializedData.m.split('/');
            state.zoomLevel = Number.parseInt(mapParts[0], 10);
            state.x = +mapParts[1];
            state.y = +mapParts[2];
        }
        if ('a' in serializedData) {
            state.articleId = Number.parseInt(serializedData.a, 10);
        } else if ('p' in serializedData) {
            state.popupId = Number.parseInt(serializedData.p, 10);
        }
        return state;
    };
    let mapStateTimeoutId; // ES6: let
    const saveMapState = () => {
        const serializedStateId = window.location.hash.replace(/^#/, '');
        const serializedState = serializedStateId
            ? deserializeMapState(serializedStateId)
            : null;
        const prevState =
            history.state !== null ? history.state : serializedState;
        const state = getMapState();
        const stateId = serializeMapState(state);
        if (equalStates(state, prevState)) {
            history.replaceState(state, '', `#${stateId}`);
        } else {
            history.pushState(state, '', `#${stateId}`);
            if (cookieConsent.hasConsented('essential')) {
                cookie.create(
                    MAP_STATE_COOKIE,
                    stateId,
                    { path: mapURL }
                );
            } else {
                cookie.erase(MAP_STATE_COOKIE, {
                    path: mapURL
                });
            }
            emit(mapElem, 'map:stateChanged', [state]);
        }
    };
    const saveMapStateIn = (timeout) => {
        timeout = timeout || 0;
        clearTimeout(mapStateTimeoutId);
        mapStateTimeoutId = setTimeout(saveMapState, timeout);
    };
    const restoreMapState = (animated) => {
        const serializedState = window.location.hash.replace(/^#/, '');
        if (!serializedState) return;

        const state = deserializeMapState(serializedState);
        const stateId = serializeMapState(state);
        if (state.articleId !== undefined) {
            // Find node with article link
            const articleUrlPattern = new RegExp(
                `(#a=|\\/article\\/)${state.articleId}\\b`
            );
            let articleNodeId; // ES6: let
            nodes.some((node) => {
                const linkURL = translateStr(node.content.node_link_to);
                const isExternal = isExternalLink(linkURL);
                const isArticle = articleUrlPattern.test(linkURL);
                if (!isExternal && isArticle) {
                    articleNodeId = String(node.id);
                    return true; // Stop loop
                }
                return false;
            });
            // Redirect to the popup that links to article
            if (articleNodeId !== undefined) {
                // Go to node URL
                window.location.hash = `#p=${articleNodeId}`;
            } else {
                history.replaceState(state, '', `#${stateId}`);
                const articleURL = `${mapURL}/article/${state.articleId}?embed=0`;
                if (accessToken) {
                    window.location.assign(
                        injectParams(articleURL, {
                            access: accessToken
                        })
                    );
                } else {
                    window.location.assign(articleURL);
                }
            }
        } else if (state.popupId !== undefined) {
            const source = nodeElemsById[String(state.popupId)] || null;
            if (!source) {
                // A hash naming a point this map no longer has: an old
                // bookmark, or a link to one since deleted. Reading on
                // from here threw and abandoned the rest of the restore,
                // so the map never reached the position it was asked
                // for. The map itself is still what the visitor wanted.
                console.error(`Node not found: ${state.popupId}`);
                popupManager.hide();
                popupPanel.hide();
                setMapState(state, animated);
                return;
            }

            if (source.classList.contains('has-popup')) {
                const node = source.__data__;
                if (source.classList.contains('has-popup-panel')) {
                    const linkURL = translateStr(node.content.node_link_to);
                    const articleMatch = matchArticleLink(linkURL);
                    const options = {};
                    if (state.zoomLevel !== undefined)
                        options.zoomLevel = +state.zoomLevel;
                    if (articleMatch) {
                        let articleMapURL = getRelativeLink(
                            articleMatch[1]
                        ); // ES6: let
                        const articleId = articleMatch[3];
                        articleMapURL =
                            stripPathForApp(articleMapURL);
                        options.externalContent = `${articleMapURL.replace(/#.*$/, '')}/article/${articleId}`;
                        options.popupType = 'popup-panel-full';
                    } else if (
                        node.content.popup_type === 'popup-panel-large'
                    ) {
                        options.popupType = node.content.popup_type;
                    }
                    if (
                        translateStr(node.content.external_content_url)
                            ?.length
                    ) {
                        options.externalContent = `${translateStr(node.content.external_content_url)}`;
                    }
                    if (options.externalContent && accessToken) {
                        options.externalContent = injectParams(
                            options.externalContent,
                            { access: accessToken }
                        );
                    }
                    options.moveFocus = !!animated;
                    popupPanel.showFor(source, options);
                    curPopupElem = source;
                    setExpandedPoint(source);
                } else if (
                    source.classList.contains('has-popup-detached')
                ) {
                    const options2 = {};
                    const popupType = node.content.popup_type;
                    if (
                        popupType === 'popup' ||
                        popupType === 'popup-medium' ||
                        popupType === 'popup-small'
                    ) {
                        options2.popupType =
                            popupType === 'popup'
                                ? 'popup-large'
                                : popupType;
                    }
                    if (state.zoomLevel !== undefined)
                        options2.zoomLevel = +state.zoomLevel;
                    options2.moveFocus = !!animated;
                    popupManager.showFor(source, options2);
                    curPopupElem = source;
                    setExpandedPoint(source);
                }
            } else {
                const linkURL2 = source.dataset.href;
                const linkTarget2 = source.dataset.target;
                if (linkURL2) {
                    if (
                        cookieConsent.hasConsented('essential')
                    ) {
                        cookie.create(
                            MAP_STATE_COOKIE,
                            stateId,
                            { path: mapURL }
                        );
                    } else {
                        cookie.erase(
                            MAP_STATE_COOKIE,
                            { path: mapURL }
                        );
                    }
                    history.replaceState(state, '', `#${stateId}`);
                    if (linkTarget2) {
                        window.open(linkURL2, linkTarget2);
                    } else {
                        window.location.assign(linkURL2);
                    }
                }
            }
        } else {
            // No popup or article in map state, so close all existing popups
            popupManager.hide();
            popupPanel.hide();
            setMapState(state, animated);
        }
    };

    on('cookieConsent:revoke:essential', () => {
        cookie.erase(MAP_STATE_COOKIE);
    });

    on('map:zoom', () => {
        saveMapStateIn(900);
    });
    window.addEventListener('popstate', (event) => {
        const state = event.state;
        if (state) setMapState(state, true);
    });

    restoreMapState();
    window.addEventListener('hashchange', () => {
        restoreMapState(true);
    });

    const parentWindow = window.parent;
    on('map:stateChanged', (state) => {
        const stateHash = `#${serializeMapState(state)}`;
        if (isEmbedded) {
            parentWindow.postMessage(stateHash, '*');
        }
    });
    window.addEventListener('keydown', (event) => {
        const key = event.key;
        if (isEmbedded && (key === 'Esc' || key === 'Escape')) {
            parentWindow.postMessage('kontextlab:embedCloseRequest', '*');
        }
    });

    /* Track visited nodes, mark them and save the state */
    let visitedNodes = []; // ES6: let
    const markVisitedNodes = !!cfg.map.markReadContent;
    const loadVisitedNodes = () => {
        let cookieVal = '[]'; // ES6: let
        if (cookieConsent.hasConsented('essential')) {
            cookieVal = cookie.read(
                NODES_VISITED_LIST_COOKIE
            );
        }
        if (cookieVal) {
            visitedNodes = JSON.parse(cookieVal);
        } else {
            const deprecatedCookieVal =
                cookie.read('nodesVisited');
            const visitedNodesByMap = deprecatedCookieVal
                ? JSON.parse(deprecatedCookieVal)
                : {};
            if (Object.hasOwn(visitedNodesByMap, mapId)) {
                visitedNodes = visitedNodesByMap[mapId];
            }
        }
    };
    const saveVisitedNode = (nodeId) => {
        if (visitedNodes.indexOf(nodeId) !== -1) return;
        visitedNodes.push(nodeId);
        if (cookieConsent.hasConsented('essential')) {
            cookie.create(
                NODES_VISITED_LIST_COOKIE,
                JSON.stringify(visitedNodes),
                { path: mapURL }
            );
        } else {
            cookie.erase(NODES_VISITED_LIST_COOKIE, {
                path: mapURL
            });
        }
    };
    const initiateVisitedNodes = () => {
        loadVisitedNodes();
        Object.keys(nodes).forEach((nodeId) => {
            const node = nodes[nodeId];
            const nodeElem = nodeElemsById[nodeId];
            if (
                !nodeElem ||
                (!translateStr(node.content.content) &&
                    !translateStr(node.content.marginal_content_1) &&
                    !translateStr(node.content.marginal_content_2) &&
                    !translateStr(node.content.node_link_to))
            ) {
                if (!nodeElem) {
                    console.warn(
                        `Could not find DOM element for node with id ${nodeId}`
                    );
                }
                return;
            }
            nodeElem.addEventListener('click', () => {
                nodeElem.classList.add('visited');
                saveVisitedNode(nodeId);
            });
            if (visitedNodes.indexOf(nodeId) !== -1)
                nodeElem.classList.add('visited');
        });
    };

    if (markVisitedNodes) {
        initiateVisitedNodes();
        on('cookieConsent:allow:essential', loadVisitedNodes);
        on('cookieConsent:revoke:essential', () => {
            cookie.erase(NODES_VISITED_LIST_COOKIE);
        });
    }

    /* Map zoom indication */
    let stopZoomTimeoutId; // ES6: let
    const startZooming = () => {
        mapElem.classList.add('is-zooming');
    };
    const stopZooming = () => {
        clearTimeout(stopZoomTimeoutId);
        stopZoomTimeoutId = setTimeout(() => {
            mapElem.classList.remove('is-zooming');
            stopZoomTimeoutId = null;
        }, 100);
    };
    if (browser.isChrome) {
        on('mousewheel', startZooming);
    on('map:zoom', stopZooming);
        mapElem.addEventListener(
            'wheel',
            () => {
                startZooming();
            },
            { passive: true }
        );
    }

    // Map controls
    document
        .querySelectorAll('.controls, .search-box, .toc-box')
        .forEach((el) => {
            ['mousedown', 'touchstart', 'wheel'].forEach((evt) => {
                el.addEventListener(
                    evt,
                    (event) => {
                        event.stopPropagation();
                    },
                    { passive: false }
                );
            });
        });

    const zoomControls = document.querySelector('.zoom-controls');
    if (zoomControls) {
        const zoomToPage = zoomControls.querySelector('.zoom-to-page');
        if (zoomToPage) {
            zoomToPage.addEventListener('click', (ev) => {
                startZooming();
                zoomTo(1)(ev);
            });
            zoomToPage.addEventListener('touchstart', (ev) => {
                startZooming();
                zoomTo(1)(ev);
            });
        }

        const zoomIn = zoomControls.querySelector('.zoom-in');
        if (zoomIn) {
            zoomIn.addEventListener('click', (ev) => {
                startZooming();
                zoomBy(1)(ev);
            });
            zoomIn.addEventListener('touchstart', (ev) => {
                startZooming();
                zoomBy(1)(ev);
            });
        }

        const zoomOut = zoomControls.querySelector('.zoom-out');
        if (zoomOut) {
            zoomOut.addEventListener('click', (ev) => {
                startZooming();
                zoomBy(-1)(ev);
            });
            zoomOut.addEventListener('touchstart', (ev) => {
                startZooming();
                zoomBy(-1)(ev);
            });
        }
    }
    document.querySelectorAll('.zoom-range a').forEach((a) => {
        a.addEventListener('click', (ev) => {
            startZooming();
            zoomTo(+a.textContent)(ev);
        });
        a.addEventListener('touchstart', (ev) => {
            startZooming();
            zoomTo(+a.textContent)(ev);
        });
    });

    /* Language switch */
    document.querySelectorAll('.language-selection a').forEach((a) => {
        a.addEventListener('click', function (event) {
            event.preventDefault();
            window.location.assign(
                this.href + window.location.hash.replace(/#$/, '')
            );
        });
    });

    /* List of related links to popups and articles */
    const tocLinks = document.querySelector('.toc-links');
    const tocList = tocLinks?.querySelector('.toc-links-list');
    const popupBaseUrl = tocLinks?.dataset.popupBaseUrl;
    const articleBaseUrl = tocLinks?.dataset.articleBaseUrl;
    if (tocList && tocList.childElementCount === 0) {
        Object.keys(nodes).forEach((nodeId) => {
            const node = nodes[nodeId];
            if (!nodeHasContent(node)) {
                return;
            }
            const linkUrl = translateStr(node.content.node_link_to);
            const articleMatch = matchArticleLink(linkUrl);

            // A bare `#p=` hash goes through the map's own hash router,
            // which opens the point in place with focus managed. The
            // per-point page it used to link to bounces every browser
            // straight back to this map, so following one cost a full
            // page load and landed the reader where they started.
            const href = linkUrl
                ? articleMatch
                    ? articleBaseUrl.replace(/\/0/, `/${articleMatch[3]}`)
                    : linkUrl
                : `#p=${nodeId}`;

            const a = document.createElement('a');
            a.href = href;
            a.textContent =
                smartStripLineBreaks(
                    translateStr(node.content.title)
                ) ||
                cfg.uiText?.a11y?.unnamedPoint ||
                '';
            const li = document.createElement('li');
            li.appendChild(a);
            tocList.appendChild(li);
        });
    }

    // --- Analytics ---
    const sendAnalyticsEvent = (category, action, label) => {
        if (cfg.analytics?.provider === 'google') {
            // Prefer GA4/UA if gtag exists
            if (window.gtag) {
                window.gtag('event', action, {
                    event_category: category,
                    event_label: label,
                    anonymize_ip: true
                });
                return true;
            }
            // Google Tag Manager dataLayer
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'custom_event',
                    event_category: category,
                    event_action: action,
                    event_label: label
                });
                return true;
            }
        }
        // Matomo
        if (cfg.analytics?.provider === 'matomo' && window._paq) {
            window._paq.push(['trackEvent', category, action, label]);
            return true;
        }
        // PostHog
        if (cfg.analytics?.provider === 'posthog') {
            // The provider's own snippet defines this; the backend
            // renders it only for a tenant that uses PostHog.
            window.posthog?.capture(action, { category, label });
            return true;
        }
        return false;
    };
    if (cfg.analytics?.enabled) {
        on('loaded.map', () => {
            const mapName = cfg.map.name || '';
            onTarget(popupPanel, 'show.popup', () => {
                const title =
                    document
                        .querySelector('.popup-panel.active h1')
                        ?.textContent?.trim() || '';
                const popupName = `${mapName}: ${title} (${popupPanel.sourceId})`;
                sendAnalyticsEvent('engagement', 'show_popup', popupName);
            });
            onTarget(popupManager, 'show.popup', () => {
                const title =
                    document
                        .querySelector('.popup-detached.active h1')
                        ?.textContent?.trim() || '';
                const popupName = `${mapName}: ${title} (${popupManager.sourceId})`;
                sendAnalyticsEvent('engagement', 'show_popup', popupName);
            });
            let prevZoom;
            on('map:stateChanged', (state) => {
                if (state.zoomLevel !== prevZoom) {
                    const label = `${mapName}: ${state.zoomLevel}`;
                    sendAnalyticsEvent('engagement', 'zoom', label);
                }
                prevZoom = state.zoomLevel;
            });
        });
    }

    emit(mapElem, 'loaded.map', []);
}

    const parentWindow = window.parent;
    let mapReadyEventSent = false; // ES6: let
    const metaDescription =
        document.querySelector('meta[name="description"]') || null;
    const metaDescContent = metaDescription
        ? metaDescription.getAttribute('content')
        : '';
    let metaDescEventSent = false; // ES6: let
    window.addEventListener('message', (event) => {
        const messageData = event.data;
        if (messageData !== null && typeof messageData === 'object') {
            if ('hash' in messageData) {
                window.location.hash = messageData.hash;
            }
            if ('access' in messageData) {
                cookie.create(
                    'access_token',
                    `${messageData.access}`,
                    {
                        path: location.pathname === '/' ? '' : location.pathname
                    }
                );
            }
            if ('embedDomain' in messageData) {
                const allowed = (
                    getConfig()?.map?.embedDomains || []
                ).map((d) => String(d).toLowerCase());
                const domain = String(
                    messageData.embedDomain || ''
                ).toLowerCase();
                const ok = allowed.some(
                    (suffix) =>
                        domain === suffix || domain.endsWith(`.${suffix}`)
                );
                if (allowed.length && !ok) {
                    const mapModel = document.querySelector('.map-model');
                    if (mapModel) mapModel.style.display = 'none';
                    setTimeout(() => {
                        if (mapModel) mapModel.innerHTML = '';
                        alert('This map cannot be used on this domain.');
                    }, 100);
                }
            }
            if ('embedUrl' in messageData) {
                if (
                    getPopupInstances().manager &&
                    typeof getPopupInstances().manager
                        .setSocialMediaBaseURL === 'function'
                ) {
                    getPopupInstances().manager.setSocialMediaBaseURL(
                        messageData.embedUrl
                    );
                }
                if (
                    getPopupInstances().panel &&
                    typeof getPopupInstances().panel
                        .setSocialMediaBaseURL === 'function'
                ) {
                    getPopupInstances().panel.setSocialMediaBaseURL(
                        messageData.embedUrl
                    );
                }
            }
        }
        if (!mapReadyEventSent) {
            parentWindow.postMessage('kontextlab:mapReady', '*');
            mapReadyEventSent = true;
        }
        if (!metaDescEventSent && metaDescContent) {
            parentWindow.postMessage(
                `kontextlab:metaData::${metaDescContent}`,
                '*'
            );
            metaDescEventSent = true;
        }
    });

    /* Menu */
    const burgerMenu = document.getElementById('menu');
    initMenu(
        document.querySelector('footer a[href="#menu"]'),
        burgerMenu
    );
